import { h, createContext } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';
import Storefront from 'utils/storefront';
import { productByHandle, productsByTag } from 'utils/storefront/queries';

const Context = createContext();

export const useProduct = () => useContext(Context);

const ProductContext = ({ data, children }) => {
  const [handle, setActiveHandle] = useState(data.product.handle);
  const [activeProduct, setActiveProduct] = useState(null);
  const [allLengths, setAllLengths] = useState(null);
  const [activeLength, setActiveLength] = useState(null);
  const [activeVariant, setActiveVariant] = useState(null);
  const [allProducts, setAllProducts] = useState(null);
  const [allVariants, setAllVariants] = useState(null);
  const [allSizeOptions, setSizeOptions] = useState(null);
  const [hasChanged, setHasChanged] = useState(false);
  const [productModels, setProductModels] = useState(null);
  const [productModelMedias, setProductModelMedias] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);

  const [skipLengthValidation, setSkipLengthValidation] = useState(true);
  const { optionsWithValues, isKit, groupTag } = data;

  const addSessionParameter = (param, value) => {
    sessionStorage.setItem(param, value);
  };

  const getSessionParameter = (param) => sessionStorage.getItem(param);

  const optionsObject = optionsWithValues.reduce((acc, option) => {
    acc[option.name] = {
      name: option.name,
      values: option.values,
    };
    return acc;
  }, {});

  const initialVariant = data.product.variants.find(
    (variant) => variant.id === data.selectedOrFirstAvailableVariantId,
  );

  const [selectedFit, setSelectedFit] = useState(
    new URL(window.location.href).searchParams.get('fit') || 'regular',
  );

  const [selectedOptions, setSelectedOptions] = useState({
    Size: getSessionParameter('size') || initialVariant?.option1,
    Color: initialVariant?.option2,
  });

  if (optionsObject.hasOwnProperty('Length')) {
    setSkipLengthValidation(false);
  }

  const handleSwatchUrl = (value) =>
    `//cdn.shopify.com/s/files/1/1011/2122/files/swatch--${value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-$/, '')
      .replace(/^-/, '')}_100x100.png`;

  useEffect(() => {
    if (isKit) return;
    if (!allVariants) return;

    const sizeOptions = allVariants.map((a) => a.node.title.split(' / ')[0]);
    const sizeOptionConstructor = {
      name: 'Size',
      values: [],
    };
    sizeOptionConstructor.values = sizeOptions;
    setSizeOptions(sizeOptionConstructor);

    const newVariantSameSize =
      allVariants.find(
        (variant) =>
          variant?.node?.selectedOptions[0]?.value === selectedOptions.Size &&
          variant?.node?.selectedOptions[1]?.value === selectedOptions.Color &&
          (skipLengthValidation ||
            variant?.node?.selectedOptions[2]?.value === selectedOptions.Length),
      ) || null;

    if (newVariantSameSize) {
      setActiveVariant(newVariantSameSize.node);
    } else {
      setActiveVariant(allVariants[0].node);
    }
  }, [selectedOptions]);

  useEffect(() => {
    if (!allProducts || isKit) return;
    const newActiveProduct = allProducts.find(
      (product) => product.handle === handle,
    );
    if (newActiveProduct.productType === 'Accessories: Gift Card') return;
    setAllVariants(newActiveProduct.variants.edges);
    setActiveProduct(newActiveProduct);
    setSelectedOptions({
      ...selectedOptions,
      Color: newActiveProduct?.variants?.edges[0]?.node?.selectedOptions[1]?.value,
    });

    // Replace product handle in URL with new handle
    const url = new URL(window.location.href);
    url.pathname = `/products/${newActiveProduct.handle}`;
    url.searchParams.set(
      'variant',
      activeVariant.id.split('gid://shopify/ProductVariant/')[1],
    );
    window.history.replaceState({}, '', url);
  }, [handle, allProducts]);

  useEffect(async () => {
    const storefront = new Storefront();

    const fetchColorGroupsByTag = async () => {
      const responseJson = await storefront.request({
        query: productsByTag(groupTag),
      });

      const filteredResponseJson = responseJson.data.products.nodes
        .filter((t) => t != null)
        .map((product) => {
          let swatchUrl = null;
          if (product.variants.edges[0].node.selectedOptions[1]) {
            swatchUrl = handleSwatchUrl(
              product.variants.edges[0].node.selectedOptions[1].value,
            );
          }

          return {
            ...product,
            swatchUrl,
            lengthTag: product.tags.find((tag) => tag.includes('length::')),
          };
        });

      // The following moves the current Product to the
      // top of the array
      const currentProduct = filteredResponseJson.find(
        (product) => product.handle === handle,
      );
      const currentProductIndex = filteredResponseJson.indexOf(currentProduct);
      filteredResponseJson.splice(currentProductIndex, 1);
      filteredResponseJson.unshift(currentProduct);

      return filteredResponseJson;
    };

    const fetchProductByHandle = async () => {
      const responseJson = await storefront.request({
        query: productByHandle(),
        variables: { handle },
      });
      const productData = responseJson.data.productByHandle;

      let swatchUrl = null;
      if (productData.variants.edges[0].node.selectedOptions[1]) {
        swatchUrl = handleSwatchUrl(
          productData.variants.edges[0].node.selectedOptions[1].value,
        );
      }

      const formattedData = {
        ...productData,
        swatchUrl,
        lengthTag: productData.tags.find((tag) => tag.includes('length::')),
      };

      return [formattedData];
    };

    const products = groupTag
      ? await fetchColorGroupsByTag()
      : await fetchProductByHandle();

    let unique = [];
    products.forEach((element) => {
      if (!element?.tags.filter((tag) => tag.includes('length'))[0]) return;
      let tag = element.tags.filter((tag) => tag.includes('length'))[0];

      if (!unique.includes(tag)) {
        unique.push(tag);
      }
    });

    unique.sort((a, b) =>
      a.localeCompare(b, navigator.languages[0] || navigator.language, {
        numeric: true,
        ignorePunctuation: true,
      }),
    );

    const activeLength =
      products[0]?.tags.find((a) => a.includes('length')) ?? '';

    // finds all product variants with a model and extracts the model details to show in product-models
    const models = products[0]?.variants.edges
      .filter((variant) => variant.node.model)
      .map((variant) => variant.node.model.reference.fields);

    const modelsDetails =
      models?.map((model) =>
        model.reduce((acc, field) => {
          acc[field.key] = field.value;
          return acc;
        }, {}),
      ) || [];

    const firstModelVariant = products[0]?.variants.edges.find(
      (variant) => variant.node.model,
    );

    const modelVariantImages =
      firstModelVariant?.node.modelImages.references.nodes.map((node) => ({
        node,
      }));

    // Ensure the first model variant is selected when switching products
    const firstAvailableVariant =
      firstModelVariant?.node || products[0]?.variants.edges[0]?.node;

    // Find variant that matches selectedOptions
    const matchingVariant = products[0]?.variants.edges.find(
      (variant) =>
        variant?.node?.selectedOptions[0]?.value === selectedOptions.Size
        && variant?.node?.selectedOptions[1]?.value === selectedOptions.Color
        && (skipLengthValidation
          || variant?.node?.selectedOptions[2]?.value === selectedOptions.Length),
    );

    setAllProducts(products);
    setAllVariants(products[0]?.variants.edges);
    setProductModels(modelsDetails.length > 0 ? modelsDetails : []);
    setProductModelMedias(modelVariantImages);
    setActiveVariant(matchingVariant?.node || firstAvailableVariant);
    setActiveProduct(products[0]);
    setActiveLength(activeLength);
    setAllLengths(unique);
  }, []);

  useEffect(async () => {
    const storefront = new Storefront();

    const fetchColorGroupsByTag = async () => {
      const responseJson = await storefront.request({
        query: productsByTag(groupTag),
      });

      const filteredResponseJson = responseJson.data.products.nodes
        .filter((t) => t != null)
        .map((product) => {
          let swatchUrl = null;
          if (product.variants.edges[0].node.selectedOptions[1]) {
            swatchUrl = handleSwatchUrl(
              product.variants.edges[0].node.selectedOptions[1].value,
            );
          }

          return {
            ...product,
            swatchUrl,
            lengthTag: product.tags.find((tag) => tag.includes('length::')),
          };
        });

      // The following moves the current Product to the
      // top of the array
      const currentProduct = filteredResponseJson.find(
        (product) => product.handle === handle,
      );
      const currentProductIndex = filteredResponseJson.indexOf(currentProduct);
      filteredResponseJson.splice(currentProductIndex, 1);
      filteredResponseJson.unshift(currentProduct);

      return filteredResponseJson;
    };

    const fetchProductByHandle = async () => {
      const responseJson = await storefront.request({
        query: productByHandle(),
        variables: { handle },
      });
      const productData = responseJson.data.productByHandle;

      let swatchUrl = null;
      if (productData.variants.edges[0].node.selectedOptions[1]) {
        swatchUrl = handleSwatchUrl(
          productData.variants.edges[0].node.selectedOptions[1].value,
        );
      }

      const formattedData = {
        ...productData,
        swatchUrl,
        lengthTag: productData.tags.find((tag) => tag.includes('length::')),
      };

      return [formattedData];
    };

    const products = groupTag
      ? await fetchColorGroupsByTag()
      : await fetchProductByHandle();

    let unique = [];
    products.forEach((element) => {
      if (!element?.tags.filter((tag) => tag.includes('length'))[0]) return;
      let tag = element.tags.filter((tag) => tag.includes('length'))[0];

      if (!unique.includes(tag)) {
        unique.push(tag);
      }
    });

    unique.sort((a, b) =>
      a.localeCompare(b, navigator.languages[0] || navigator.language, {
        numeric: true,
        ignorePunctuation: true,
      }),
    );

    const activeLength =
      products[0]?.tags.find((a) => a.includes('length')) ?? '';

    // finds all product variants with a model and extracts the model details to show in product-models
    const models = products[0]?.variants.edges
      .filter((variant) => variant.node.model)
      .map((variant) => variant.node.model.reference.fields);

    const modelsDetails =
      models?.map((model) =>
        model.reduce((acc, field) => {
          acc[field.key] = field.value;
          return acc;
        }, {}),
      ) || [];

    const firstModelVariant = products[0]?.variants.edges.find(
      (variant) => variant.node.model,
    );
    const modelVariantImages =
      firstModelVariant?.node.modelImages.references.nodes.map((node) => ({
        node,
      }));

    // Ensure the first model variant is selected when switching products
    const firstAvailableVariant =
      firstModelVariant?.node || products[0]?.variants.edges[0]?.node;

    setProductModels(modelsDetails.length > 0 ? modelsDetails : []);
    setProductModelMedias(modelVariantImages);
    setActiveVariant(firstAvailableVariant);
  }, [handle]);

  // Add variant ID to the URL when the variant changes
  useEffect(() => {
    if (!activeVariant || isKit) return;
    const url = new URL(window.location.href);
    url.searchParams.set(
      'variant',
      activeVariant.id.split('gid://shopify/ProductVariant/')[1],
    );
    window.history.replaceState({}, '', url);
  }, [activeVariant]);

  // Initial update to swap product medias for first model medias
  useEffect(() => {
    if (activeProduct && productModelMedias) {
      setActiveProduct({
        ...activeProduct,
        media: { edges: productModelMedias },
      });
    }
  }, [productModelMedias]);

  // Update product medias when a new model is selected
  useEffect(() => {
    if (activeProduct) {
      const selectedModelVariant = activeProduct?.variants.edges.find(
        (variant) => {
          if (variant.node.model) {
            return variant.node.model.reference.fields.find(
              (field) =>
                field.key === 'name' && field.value === selectedModel.name,
            );
          }
        },
      );

      const modelVariantImages =
        selectedModelVariant?.node.modelImages.references.nodes.map((node) => ({
          node,
        }));

      setActiveProduct({
        ...activeProduct,
        media: { edges: modelVariantImages },
      });
    }
  }, [selectedModel]);

  /**
   * Update the main product when the swatch/color changes
   * @param {String} handle
   */
  const handleProductChange = (handle) => {
    setActiveHandle(handle);
    const newProduct = allProducts.find((product) => product.handle === handle);
    const newVariantSameSize =
      newProduct.variants.edges.find(
        (variant) =>
          variant.node.selectedOptions[0].value === selectedOptions.Size &&
          (skipLengthValidation ||
            variant.node.selectedOptions[2].value === selectedOptions.Length),
      ) || null;

    if (newVariantSameSize) {
      setActiveVariant(newVariantSameSize.node);
    } else {
      setActiveVariant(newProduct.variants.edges[0].node);
    }
    document.dispatchEvent(new CustomEvent('checkRebuyPosition'));
  };

  /**
   * Update the selected fit when a fit selector (normal or tall) is selected
   * @param {String} value
   */
  const handleFitChange = (value) => {
    if (value === selectedFit) return;
    setSelectedFit(value);

    const url = new URL(window.location.href);
    url.searchParams.set('fit', value);
    window.history.replaceState({}, '', url);
  };

  /**
   * Update the selected options when a variant (length or size) is selected
   * @param {String} value
   * @param {String} name
   */
  const handleVariantChange = (value, name) => {
    if (name === 'Size') {
      addSessionParameter('size', value);
    }
    if (value === selectedOptions[name]) return;
    setSelectedOptions({ ...selectedOptions, [name]: value });
    document.dispatchEvent(new CustomEvent('checkRebuyPosition'));
  };

  return (
    <Context.Provider
      value={{
        activeProduct,
        data,
        setActiveHandle,
        allProducts,
        activeVariant,
        setActiveVariant,
        hasChanged,
        setHasChanged,
        allVariants,
        activeLength,
        setActiveLength,
        lengthOptions: optionsObject?.Length,
        sizeOptions: allSizeOptions || optionsObject?.Size,
        colorOptions: optionsObject?.Color,
        allLengths,
        selectedFit,
        selectedOptions,
        productModels,
        setSelectedModel,
        handleFitChange,
        handleVariantChange,
        handleProductChange,
        skipLengthValidation,
        yotpoApiKey: data.yotpoApiKey,
        customer: data.customer,
        isKit,
        sizeGuides: data.sizeGuides,
        stickyCTA: data.stickyCTA,
        thumbnailSwatches: data.thumbnailSwatches,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default ProductContext;
